import { InboxOutlined } from '@ant-design/icons';
import { Grid, message, Upload, type UploadProps } from 'antd';
import type { RcFile } from 'antd/lib/upload';
import { useField } from 'formik';
import { isObject } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { type UploadFileWithPreviewFormDataFragment } from '../../api/fragments/UploadFileWithPreviewFormData';
import { formatExtensions, validateExtensions } from '../../utilities/extensions';
import FormItem, { type FormItemProps } from './FormItem';

const { Dragger } = Upload;

const StyledDragger = styled(Dragger)`
    padding: 0 10px;
`;

export type DraggerPlaceholderProps = {
    extensions: string[];
    sizeLimitInMiB?: number;
    recommendedImageSize?: string;
};

export const DraggerPlaceholder = ({
    extensions,
    sizeLimitInMiB = 2,
    recommendedImageSize = '1920x1080px',
}: DraggerPlaceholderProps) => {
    const { t } = useTranslation(['common']);

    const formats = formatExtensions(extensions);

    return (
        <>
            <p className="ant-upload-drag-icon">
                <InboxOutlined />
            </p>
            <p className="ant-upload-text">{t('common:upload.uploadText')}</p>
            <p className="ant-upload-hint">
                {t('common:upload.uploadHint', { formats, maxFiles: 1, size: recommendedImageSize })}
            </p>
        </>
    );
};

export type DraggerFieldProps = Omit<UploadProps, 'listType' | 'maxCount' | 'multiple' | 'accept' | 'defaultFileList'> &
    DraggerPlaceholderProps & {
        name: string;
        extensions: string[];
        label?: string;
        required?: boolean;
        tooltip?: FormItemProps['tooltip'];
        children?: React.ReactNode;
        onSuccess?: (file: string | File | RcFile, url: string) => void;
    };

const SingleDraggerField = ({
    name,
    label,
    extensions,
    required = false,
    sizeLimitInMiB = 2,
    recommendedImageSize = '1920x1080px',
    tooltip,
    onSuccess: onSuccessProps,
    children,
    ...props
}: DraggerFieldProps) => {
    const { t } = useTranslation(['common']);

    const accept = useMemo(() => extensions.join(','), [extensions]);
    const [, meta, helpers] = useField<File | UploadFileWithPreviewFormDataFragment | null>(name);

    const defaultFileList = useMemo(() => {
        if (!(meta.initialValue instanceof File) && isObject(meta.initialValue)) {
            const { initialValue } = meta;

            // map the preview
            return [
                {
                    uid: initialValue.id,
                    name: initialValue?.filename,
                    thumbUrl: initialValue.preview?.url || initialValue?.url,
                },
            ];
        }

        return [];
    }, [meta]);

    const customRequest = useCallback<NonNullable<UploadProps['customRequest']>>(
        ({ file, onSuccess }) => {
            if (file instanceof File) {
                const reader = new FileReader();

                reader.onload = ({ target }) => {
                    if (onSuccess) {
                        onSuccess(target?.result);
                    }

                    if (onSuccessProps) {
                        onSuccessProps(file, target?.result as string); // since we know its url
                    }
                };

                reader.readAsDataURL(file as Blob);

                // set the file manually
                helpers.setValue(file);
                // touch the field manually
                helpers.setTouched(true);
            }
        },
        [helpers, onSuccessProps]
    );

    const onRemove = useCallback<NonNullable<UploadProps['onRemove']>>(() => {
        // manually touch the file
        helpers.setTouched(true);

        // delete the file
        helpers.setValue(null);
    }, [helpers]);

    const beforeUpload = useCallback<NonNullable<UploadProps['beforeUpload']>>(
        file => {
            if (file.size > sizeLimitInMiB * 1024 * 1024) {
                message.error(t('common:upload.messages.sizeExceedsLimit', { limit: sizeLimitInMiB }));

                return Upload.LIST_IGNORE;
            }

            const reader = new FileReader();
            const promise = new Promise<boolean | string>((resolve, reject) => {
                reader.onload = async event => {
                    const content = event.target.result as string;
                    const validationResult = await validateExtensions(content, file.name);
                    if (validationResult.valid) {
                        return resolve(true);
                    }
                    message.error(t('common:upload.messages.fileError'));

                    return resolve(Upload.LIST_IGNORE);
                };

                reader.onerror = event => resolve(Upload.LIST_IGNORE);
            });

            reader.readAsBinaryString(file);

            return Promise.resolve(promise);
        },
        [sizeLimitInMiB, t]
    );

    const onChange = useCallback<NonNullable<UploadProps['onChange']>>(info => {
        if (info.file.status === 'uploading') {
            // manually set to done to finish loading
            // eslint-disable-next-line no-param-reassign
            info.file.status = 'done';
        }
    }, []);

    const screens = Grid.useBreakpoint();

    return (
        <FormItem label={label} meta={meta} name={name} required={required} tooltip={tooltip}>
            <StyledDragger
                accept={accept}
                beforeUpload={beforeUpload}
                customRequest={customRequest}
                defaultFileList={defaultFileList}
                listType={screens.xs ? 'text' : 'picture'}
                maxCount={1}
                multiple={false}
                onChange={onChange}
                onRemove={onRemove}
                {...props}
            >
                {children ?? (
                    <DraggerPlaceholder
                        extensions={extensions}
                        recommendedImageSize={recommendedImageSize}
                        sizeLimitInMiB={sizeLimitInMiB}
                    />
                )}
            </StyledDragger>
        </FormItem>
    );
};

export default SingleDraggerField;
